/**
 * Admin read/manage view for a single user, shown in a slide panel.
 *
 * Renders profile sections (contact, program, location, company, demographics,
 * pitch deck) and a danger zone for ban/unban, password reset, EIR assignment,
 * engagement status, and deletion. All mutations are delegated to the on*
 * callbacks supplied by the parent (which own the API calls); this component
 * just manages per-action loading/confirm UI. Admin accounts hide the danger
 * zone. ROLE_COLORS is exported for reuse in list views.
 */
import React, { useState } from 'react';

export const ROLE_COLORS = {
  eir:     { bg: '#e8f4fd', text: '#1a5276' },
  ceg:     { bg: 'var(--color-mint-dark)', text: 'var(--color-forest)' },
  admin:   { bg: '#fdf0c2', text: '#8a6000' },
  founder: { bg: 'var(--color-blush)', text: '#7b3f3f' },
};

const ENGAGEMENT_COLORS = {
  active:   { bg: '#e6f4ea', text: '#1e6b3a' },
  dormant:  { bg: '#fff8e1', text: '#7a5c00' },
  archived: { bg: '#f5f5f5', text: '#555' },
};

export default function UserDetail({ detail, eirs = [], onEIRChange, onDelete, onBan, onUnban, onResetPassword, onEngagementStatusChange, onAddSecondaryEIR, onRemoveSecondaryEIR }) {
  const color = ROLE_COLORS[detail.role] || ROLE_COLORS.founder;
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [banning, setBanning] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [tempPassword, setTempPassword] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showTempPassword, setShowTempPassword] = useState(false);
  const [engagementSaving, setEngagementSaving] = useState(false);
  const [addingSecondaryEIR, setAddingSecondaryEIR] = useState(false);
  const [selectedSecondaryEIRId, setSelectedSecondaryEIRId] = useState('');
  const [demoOpen, setDemoOpen] = useState(false);

  // Resets the user's password server-side and surfaces the returned temporary
  // password so the admin can relay it (the user must change it on next login).
  async function handleResetPassword() {
    setResetting(true);
    setTempPassword(null);
    try {
      const result = await onResetPassword();
      setTempPassword(result.temp_password);
    } finally {
      setResetting(false);
    }
  }

  function handleCopy() {
    navigator.clipboard.writeText(tempPassword);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  async function handleDelete() {
    setDeleting(true);
    try { await onDelete(); }
    finally { setDeleting(false); }
  }

  async function handleBan() {
    setBanning(true);
    try { await onBan(); }
    finally { setBanning(false); }
  }

  async function handleUnban() {
    setBanning(true);
    try { await onUnban(); }
    finally { setBanning(false); }
  }

  async function handleEIRChange(e) {
    const val = e.target.value;
    setSaving(true);
    try { await onEIRChange(val === '' ? null : parseInt(val, 10)); }
    finally { setSaving(false); }
  }

  async function handleEngagementChange(e) {
    setEngagementSaving(true);
    try { await onEngagementStatusChange(e.target.value); }
    finally { setEngagementSaving(false); }
  }

  async function handleAddSecondaryEIR() {
    if (!selectedSecondaryEIRId) return;
    setAddingSecondaryEIR(true);
    try {
      await onAddSecondaryEIR(parseInt(selectedSecondaryEIRId, 10));
      setSelectedSecondaryEIRId('');
    } finally { setAddingSecondaryEIR(false); }
  }

  async function handleRemoveSecondaryEIR(eirId) {
    await onRemoveSecondaryEIR(eirId);
  }

  return (
    <div>
      <div style={s.row}>
        <span style={{ ...s.pill, background: color.bg, color: color.text }}>
          {detail.role.toUpperCase()}
        </span>
        <span style={{
          ...s.pill,
          background: detail.status === 'approved' ? '#e6f4ea' : detail.status === 'pending' ? '#fff8e1' : detail.status === 'banned' ? '#1a1a1a' : '#fdecea',
          color:      detail.status === 'approved' ? '#1e6b3a' : detail.status === 'pending' ? '#7a5c00' : detail.status === 'banned' ? '#ffffff' : '#b71c1c',
        }}>
          {detail.status}
        </span>
      </div>

      <Section label="Contact">
        <DetailRow label="Email" value={detail.email} />
        {detail.created_at && <DetailRow label="Joined" value={detail.created_at} />}
        {detail.profile?.phone && <DetailRow label="Phone" value={detail.profile.phone} />}
        {detail.profile?.job_title && <DetailRow label="Job title" value={detail.profile.job_title} />}
      </Section>

      {detail.profile && (
        <>
          <Section label="Program">
            {/* Engagement status */}
            <div style={s.detailRow}>
              <span style={s.detailLabel}>Engagement</span>
              <select
                style={{
                  ...s.eirSelect,
                  background: ENGAGEMENT_COLORS[detail.profile.engagement_status]?.bg || '#f5f5f5',
                  color: ENGAGEMENT_COLORS[detail.profile.engagement_status]?.text || '#555',
                }}
                value={detail.profile.engagement_status || 'active'}
                onChange={handleEngagementChange}
                disabled={engagementSaving}
              >
                <option value="active">Active</option>
                <option value="dormant">Dormant</option>
                <option value="archived">Archived</option>
              </select>
            </div>

            {/* Primary EIR */}
            <div style={s.detailRow}>
              <span style={s.detailLabel}>Primary EIR</span>
              <select
                style={s.eirSelect}
                value={detail.profile.assigned_eir?.id ?? ''}
                onChange={handleEIRChange}
                disabled={saving}
              >
                <option value="">Unassigned</option>
                {eirs.map((eir) => (
                  <option key={eir.id} value={eir.id}>
                    {eir.first_name} {eir.last_name}
                  </option>
                ))}
              </select>
            </div>

            {/* Secondary EIRs */}
            <div style={{ marginTop: 4 }}>
              <span style={s.detailLabel}>Secondary EIRs</span>
              {(detail.profile.secondary_eirs || []).length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6, marginBottom: 8 }}>
                  {detail.profile.secondary_eirs.map((seir) => (
                    <span key={seir.eir_id} style={s.secondaryEIRChip}>
                      {seir.first_name} {seir.last_name}
                      <button
                        style={s.chipRemove}
                        onClick={() => handleRemoveSecondaryEIR(seir.eir_id)}
                        title="Remove"
                      >×</button>
                    </span>
                  ))}
                </div>
              )}
              <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                <select
                  style={{ ...s.eirSelect, flex: 1 }}
                  value={selectedSecondaryEIRId}
                  onChange={(e) => setSelectedSecondaryEIRId(e.target.value)}
                >
                  <option value="">Add secondary EIR…</option>
                  {/* Offer only EIRs who aren't already the primary or a secondary. */}
                  {eirs
                    .filter((eir) =>
                      eir.id !== (detail.profile.assigned_eir?.id) &&
                      !(detail.profile.secondary_eirs || []).some((s) => s.eir_id === eir.id)
                    )
                    .map((eir) => (
                      <option key={eir.id} value={eir.id}>
                        {eir.first_name} {eir.last_name}
                      </option>
                    ))}
                </select>
                <button
                  style={s.addSecondaryBtn}
                  onClick={handleAddSecondaryEIR}
                  disabled={!selectedSecondaryEIRId || addingSecondaryEIR}
                >
                  {addingSecondaryEIR ? '…' : 'Add'}
                </button>
              </div>
            </div>
          </Section>

          {(detail.profile.address || detail.profile.city || detail.profile.county) && (
            <Section label="Location">
              {detail.profile.address && (
                <p style={s.addrBlock}>
                  {detail.profile.address}{detail.profile.address2 ? `, ${detail.profile.address2}` : ''}<br />
                  {[detail.profile.city, detail.profile.state, detail.profile.zip_code].filter(Boolean).join(', ')}
                </p>
              )}
              <DetailRow label="County" value={detail.profile.county} />
              {detail.profile.in_capital_region && (
                <div style={s.detailRow}>
                  <span style={s.detailLabel}>Capital Region</span>
                  <span style={{
                    ...s.regionBadge,
                    background: detail.profile.in_capital_region === 'Yes' ? '#dcfce7' : '#fee2e2',
                    color: detail.profile.in_capital_region === 'Yes' ? '#15803d' : '#b91c1c',
                  }}>{detail.profile.in_capital_region}</span>
                </div>
              )}
            </Section>
          )}

          {detail.profile.support_type && (
            <Section label="Support Needed">
              <p style={s.descText}>{detail.profile.support_type}</p>
            </Section>
          )}

          <Section label="Company">
            <DetailRow label="Company"  value={detail.profile.company_name} />
            <DetailRow label="Stage"    value={detail.profile.company_stage} />
            <DetailRow label="Sector"   value={detail.profile.industry} />
            {detail.profile.website && <DetailRow label="Website" value={detail.profile.website} />}
            {detail.profile.product_type && <ChipDetailRow label="Product type" value={detail.profile.product_type} />}
            <DetailRow label="Has revenue"  value={detail.profile.has_revenue} />
            <DetailRow label="Cash invested" value={detail.profile.cash_invested} />
            {detail.profile.investment_sources && <ChipDetailRow label="Investment sources" value={detail.profile.investment_sources} />}
            <DetailRow label="Amount seeking" value={detail.profile.amount_seeking} />
            {detail.profile.use_of_funds && <ChipDetailRow label="Use of funds" value={detail.profile.use_of_funds} />}
            <DetailRow label="Interested in investment" value={detail.profile.interested_in_investment} />
            <DetailRow label="In other programs"        value={detail.profile.in_other_programs} />
            {detail.profile.how_heard && <DetailRow label="How heard" value={detail.profile.how_heard} />}
            {detail.profile.description && (
              <div style={s.descBlock}>
                <p style={s.descLabel}>Value Proposition</p>
                <p style={s.descText}>{detail.profile.description}</p>
              </div>
            )}
          </Section>

          {(detail.profile.total_headcount || detail.profile.capital_region_headcount) && (
            <Section label="Employment">
              <DetailRow label="Total headcount"          value={detail.profile.total_headcount} />
              <DetailRow label="Capital Region headcount" value={detail.profile.capital_region_headcount} />
            </Section>
          )}

          {(detail.profile.is_minority_women_owned || detail.profile.is_hispanic_latino ||
            detail.profile.race_ethnicity || detail.profile.is_veteran_owned ||
            detail.profile.age_range || detail.profile.household_income) && (
            <div style={s.section}>
              <button style={s.demoToggle} onClick={() => setDemoOpen((v) => !v)}>
                {demoOpen ? '▲' : '▼'} Demographics
                <span style={s.demoNote}> — de-identified, for reporting only</span>
              </button>
              {demoOpen && (
                <div style={{ marginTop: 10 }}>
                  <DetailRow label="Minority/women-owned"  value={detail.profile.is_minority_women_owned} />
                  <DetailRow label="Hispanic/Latino origin" value={detail.profile.is_hispanic_latino} />
                  {detail.profile.race_ethnicity && <ChipDetailRow label="Race / ethnicity" value={detail.profile.race_ethnicity} />}
                  <DetailRow label="Veteran-owned"   value={detail.profile.is_veteran_owned} />
                  <DetailRow label="Age range"       value={detail.profile.age_range} />
                  <DetailRow label="Household income" value={detail.profile.household_income} />
                </div>
              )}
            </div>
          )}

          <Section label="Pitch Deck">
            {detail.profile.pitch_deck ? (
              <div style={s.pitchDeckRow}>
                <span style={s.pitchDeckName}>{detail.profile.pitch_deck.filename}</span>
                <span style={s.pitchDeckMeta}>v{detail.profile.pitch_deck.version_number}</span>
                <a
                  href={detail.profile.pitch_deck.url}
                  target="_blank"
                  rel="noreferrer"
                  style={s.pitchDeckBtn}
                >
                  Preview
                </a>
                <a
                  href={detail.profile.pitch_deck.url}
                  download={detail.profile.pitch_deck.filename}
                  style={s.pitchDeckBtn}
                >
                  Download
                </a>
              </div>
            ) : (
              <p style={s.pitchDeckEmpty}>No pitch deck found</p>
            )}
          </Section>
        </>
      )}

      {detail.role !== 'admin' && (
        <div style={s.dangerZone}>
          <div style={s.dangerRow}>
            {detail.status === 'banned' ? (
              <button style={s.unbanBtn} onClick={handleUnban} disabled={banning}>
                {banning ? 'Unbanning…' : 'Unban'}
              </button>
            ) : (
              <button style={s.banBtn} onClick={handleBan} disabled={banning}>
                {banning ? 'Banning…' : 'Ban Email'}
              </button>
            )}
            <button style={s.resetBtn} onClick={handleResetPassword} disabled={resetting}>
              {resetting ? 'Resetting…' : 'Reset Password'}
            </button>
            {!confirmDelete ? (
              <button style={s.deleteBtn} onClick={() => setConfirmDelete(true)}>
                Delete Account
              </button>
            ) : (
              <button style={s.confirmDeleteBtn} onClick={handleDelete} disabled={deleting}>
                {deleting ? 'Deleting…' : 'Confirm Delete'}
              </button>
            )}
            {confirmDelete && (
              <button style={s.cancelBtn} onClick={() => setConfirmDelete(false)} disabled={deleting}>
                Cancel
              </button>
            )}
          </div>
          {tempPassword && (
            <div style={s.tempPasswordBox}>
              <p style={s.tempPasswordLabel}>Temporary password — share this with the user:</p>
              <div style={s.tempPasswordRow}>
                <code style={s.tempPasswordCode}>
                  {showTempPassword ? tempPassword : '•'.repeat(tempPassword.length)}
                </code>
                <button style={s.copyBtn} onClick={() => setShowTempPassword((v) => !v)}>
                  {showTempPassword ? 'Hide' : 'Show'}
                </button>
                <button style={s.copyBtn} onClick={handleCopy}>{copied ? 'Copied!' : 'Copy'}</button>
              </div>
              <p style={s.tempPasswordNote}>The user will be required to set a new password on next login.</p>
            </div>
          )}
          {confirmDelete && (
            <p style={s.confirmText}>
              This will permanently delete this account and all associated data. This cannot be undone.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function Section({ label, children }) {
  return (
    <div style={s.section}>
      <p style={s.sectionLabel}>{label}</p>
      {children}
    </div>
  );
}

function DetailRow({ label, value }) {
  if (!value) return null;
  return (
    <div style={s.detailRow}>
      <span style={s.detailLabel}>{label}</span>
      <span style={s.detailValue}>{value}</span>
    </div>
  );
}

// Like DetailRow, but splits a comma-separated value into chips. Renders
// nothing if the value is empty or has no non-blank items.
function ChipDetailRow({ label, value }) {
  if (!value) return null;
  const items = value.split(',').map((v) => v.trim()).filter(Boolean);
  if (!items.length) return null;
  return (
    <div style={{ ...s.detailRow, alignItems: 'flex-start' }}>
      <span style={s.detailLabel}>{label}</span>
      <div style={s.chips}>
        {items.map((v) => <span key={v} style={s.chip}>{v}</span>)}
      </div>
    </div>
  );
}

const s = {
  row:          { display: 'flex', gap: 8, marginBottom: 20 },
  dangerZone:       { marginTop: 32, paddingTop: 20, borderTop: '1px solid var(--color-mint-dark)' },
  dangerRow:        { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 },
  banBtn:           { fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600, color: '#7a5c00', background: '#fff8e1', border: '1.5px solid #ffe082', borderRadius: 'var(--radius-sm)', padding: '7px 16px', cursor: 'pointer' },
  unbanBtn:         { fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600, color: 'var(--color-forest)', background: 'var(--color-mint)', border: '1.5px solid var(--color-mint-dark)', borderRadius: 'var(--radius-sm)', padding: '7px 16px', cursor: 'pointer' },
  resetBtn:         { fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600, color: '#1a5276', background: '#e8f4fd', border: '1.5px solid #aed6f1', borderRadius: 'var(--radius-sm)', padding: '7px 16px', cursor: 'pointer' },
  deleteBtn:        { fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600, color: '#b71c1c', background: '#fdecea', border: '1.5px solid #ef9a9a', borderRadius: 'var(--radius-sm)', padding: '7px 16px', cursor: 'pointer' },
  confirmText:      { fontFamily: 'var(--font-body)', fontSize: 12, color: '#b71c1c', marginTop: 4, lineHeight: 1.5 },
  confirmDeleteBtn: { fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 700, color: '#ffffff', background: '#c62828', border: 'none', borderRadius: 'var(--radius-sm)', padding: '7px 16px', cursor: 'pointer' },
  cancelBtn:        { fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600, color: 'var(--color-forest)', background: 'var(--color-mint)', border: '1.5px solid var(--color-mint-dark)', borderRadius: 'var(--radius-sm)', padding: '7px 16px', cursor: 'pointer' },
  tempPasswordBox:  { marginTop: 12, padding: '12px 14px', background: '#e8f4fd', borderRadius: 'var(--radius-sm)', border: '1.5px solid #aed6f1' },
  tempPasswordLabel: { fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 600, color: '#1a5276', marginBottom: 8 },
  tempPasswordRow:  { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 },
  tempPasswordCode: { fontFamily: 'monospace', fontSize: 15, fontWeight: 700, color: '#1a5276', letterSpacing: '0.05em', flex: 1 },
  copyBtn:          { fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 600, color: '#1a5276', background: '#fff', border: '1.5px solid #aed6f1', borderRadius: 'var(--radius-sm)', padding: '4px 10px', cursor: 'pointer' },
  tempPasswordNote: { fontFamily: 'var(--font-body)', fontSize: 11, color: '#1a5276', opacity: 0.75, margin: 0 },
  pill:         { padding: '3px 10px', borderRadius: 'var(--radius-full)', fontSize: 11, fontWeight: 700, letterSpacing: '0.04em' },
  section:      { marginBottom: 24 },
  sectionLabel: { fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 700, color: 'var(--color-forest)', opacity: 0.5, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 },
  detailRow:    { display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 8 },
  detailLabel:  { fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--color-forest)', opacity: 0.6 },
  detailValue:  { fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600, color: 'var(--color-forest)', textAlign: 'right', flex: 1 },
  eirSelect:    { fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600, color: 'var(--color-forest)', background: 'var(--color-mint)', border: '1.5px solid var(--color-mint-dark)', borderRadius: 'var(--radius-sm)', padding: '4px 8px', cursor: 'pointer' },
  secondaryEIRChip: { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px 3px 10px', background: '#e8f4fd', color: '#1a5276', border: '1.5px solid #aed6f1', borderRadius: 9999, fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 600 },
  chipRemove:   { background: 'none', border: 'none', color: '#1a5276', cursor: 'pointer', fontSize: 15, lineHeight: 1, padding: '0 0 0 2px', fontWeight: 700 },
  addSecondaryBtn: { fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600, color: '#1a5276', background: '#e8f4fd', border: '1.5px solid #aed6f1', borderRadius: 'var(--radius-sm)', padding: '4px 12px', cursor: 'pointer', whiteSpace: 'nowrap' },
  descBlock:      { marginTop: 8 },
  descLabel:      { fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 700, color: 'var(--color-forest)', opacity: 0.5, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 },
  descText:       { fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--color-forest)', lineHeight: 1.6, opacity: 0.85 },
  addrBlock:      { fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--color-forest)', lineHeight: 1.7, opacity: 0.85, marginBottom: 8 },
  regionBadge:    { fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 9999, whiteSpace: 'nowrap', flexShrink: 0 },
  chips:          { display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'flex-end', flex: 1 },
  chip:           { fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 600, color: 'var(--color-forest)', background: 'var(--color-mint)', border: '1px solid var(--color-mint-dark)', borderRadius: 9999, padding: '2px 8px', whiteSpace: 'nowrap' },
  demoToggle:     { background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 700, color: 'var(--color-forest)', opacity: 0.55, textTransform: 'uppercase', letterSpacing: '0.06em', padding: 0, display: 'inline-flex', alignItems: 'center', gap: 4, marginBottom: 0 },
  demoNote:       { fontFamily: 'var(--font-body)', fontSize: 10, fontWeight: 400, textTransform: 'none', letterSpacing: 0, fontStyle: 'italic', opacity: 0.85 },
  pitchDeckRow:   { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '10px 14px', background: 'var(--color-mint)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--color-mint-dark)' },
  pitchDeckName:  { flex: 1, fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600, color: 'var(--color-forest)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 },
  pitchDeckMeta:  { fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 700, color: '#15803d', background: '#dcfce7', padding: '2px 8px', borderRadius: 9999, whiteSpace: 'nowrap', flexShrink: 0 },
  pitchDeckBtn:   { fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 600, color: 'var(--color-forest)', background: '#fff', border: '1.5px solid var(--color-mint-dark)', borderRadius: 'var(--radius-sm)', padding: '4px 10px', cursor: 'pointer', textDecoration: 'none', flexShrink: 0 },
  pitchDeckEmpty: { fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--color-forest)', opacity: 0.6, fontStyle: 'italic', margin: 0 },
};
